import { ButtonGroupOptions } from '@ui-components/neo.tax';

import {
  ButtonGroupFormType,
  CheckboxFormType,
  DropdownFormType,
  FormType,
  InputFormType,
  TextAreaFormType,
  YesNo,
  YesNoFormType,
  toBoolean,
  MonthDatePickerFormType,
  MonthYearPeriodFormType,
} from '@neo.tax/shared/organisms';
import { AdditionalFormConfigOptions } from '@neo.tax/shared/types';

type CheckboxFormConfigOptions<T extends Set<string>> = Omit<
  CheckboxFormType,
  'formType' | 'onChange' | 'disabled' | 'trailingButton'
> &
  AdditionalFormConfigOptions<T>;
export const makeCheckboxFormConfig = ({
  updateQuestions,
  ...options
}: CheckboxFormConfigOptions<Set<string>>): CheckboxFormType => ({
  formType: FormType.CHECKBOXES,
  onChange(value: Set<string>): void {
    updateQuestions?.({
      [this.key]: value,
    });
  },
  ...options,
});

type ButtonGroupFormConfigOptions<T extends string> = Omit<ButtonGroupFormType, 'formType' | 'onChange'> &
  AdditionalFormConfigOptions<T>;
export const makeButtonGroupFormConfig = ({
  updateQuestions,
  ...options
}: ButtonGroupFormConfigOptions<string>): ButtonGroupFormType => ({
  formType: FormType.BUTTON_GROUP,
  onChange(value: string): void {
    updateQuestions?.({
      [this.key]: value,
    });
  },
  ...options,
});

type DropdownFormConfigOptions<T> = Omit<DropdownFormType, 'formType' | 'onChange' | 'disabled' | 'button'> &
  AdditionalFormConfigOptions<T>;
export const makeDropdownFormConfig = ({
  updateQuestions,
  ...options
}: DropdownFormConfigOptions<string | number>): DropdownFormType => ({
  formType: FormType.DROPDOWN,
  onChange(value: string | number): void {
    updateQuestions?.(
      {
        [this.key]: value,
      },
      Boolean(this.validator?.(value)),
    );
  },
  ...options,
});

type MonthDatePickerConfigOptions<T> = Omit<MonthDatePickerFormType, 'formType' | 'onChange'> &
  AdditionalFormConfigOptions<T>;
export const makeMonthDatePickerFormConfig = ({
  updateQuestions,
  ...options
}: MonthDatePickerConfigOptions<string>): MonthDatePickerFormType => ({
  formType: FormType.MONTH_DATE_PICKER,
  onChange(value: string): void {
    updateQuestions?.({
      [this.key]: value,
    });
  },
  ...options,
});

type InputFormConfigOptions<T extends string> = Omit<
  InputFormType,
  'formType' | 'onChange' | 'disabled' | 'trailingButton' | 'getErrorText'
> &
  AdditionalFormConfigOptions<T>;
export const makeInputFormConfig = ({
  updateQuestions,
  ...options
}: InputFormConfigOptions<string>): InputFormType => ({
  formType: FormType.INPUT,
  onChange(value: string): void {
    updateQuestions?.(
      {
        [this.key]: value,
      },
      Boolean(this.validator?.(value)),
    );
  },
  getErrorText: (error: string | undefined): string | undefined => error,
  ...options,
});

type TextAreaFormConfigOptions<T extends string> = Omit<
  TextAreaFormType,
  'formType' | 'onChange' | 'disabled' | 'textAreaButton'
> &
  AdditionalFormConfigOptions<T>;
export const makeTextAreaFormConfig = ({
  updateQuestions,
  ...options
}: TextAreaFormConfigOptions<string>): TextAreaFormType => ({
  formType: FormType.TEXT_AREA,
  onChange(value: string): void {
    updateQuestions?.({
      [this.key]: value,
    });
  },
  ...options,
});

type YesNoFormConfigOptions = Omit<YesNoFormType, 'formType' | 'onChange' | 'options'> &
  AdditionalFormConfigOptions<YesNo>;

export const makeYesNoFormConfig = ({ updateQuestions, ...options }: YesNoFormConfigOptions): YesNoFormType => ({
  formType: FormType.YES_NO,
  onChange(value: YesNo): void {
    const newValue = toBoolean(value);
    updateQuestions?.({
      [this.key]: newValue,
    });
  },
  get options(): ButtonGroupOptions<YesNo>[] {
    return [
      { value: YesNo.YES, label: 'Yes' },
      { value: YesNo.NO, label: 'No' },
    ];
  },
  ...options,
});

type MonthYearPeriodConfigOptions = Omit<MonthYearPeriodFormType, 'formType'>;

export const makeMonthYearPeriodFormConfig = ({
  onSubmit,
  ...options
}: MonthYearPeriodConfigOptions): MonthYearPeriodFormType => ({
  formType: FormType.MONTH_YEAR_PERIOD,
  onSubmit: onSubmit,
  ...options,
});
